/** The lab behind a model, which decides the program that runs it (runner/drivers): Claude Code or Codex. */
export type ModelVendor = "anthropic" | "openai";

export type ModelOption = { id: string; label: string; description: string; vendor: ModelVendor };

export const VENDOR_LABEL: Record<ModelVendor, string> = { anthropic: "Anthropic", openai: "OpenAI" };

/**
 * What a person reads for a login agents' own runs speak with (a `model_accounts` row, AIT-118). The words live here
 * alone because they may change again; code, API fields and tables keep "subscription". A person's own Claude plan on
 * My connections keeps its own words.
 */
export const AGENT_LOGIN = { title: "Agents' logins", many: "agents' logins", one: "agent login", a: "an agent login", add: "Add a login" } as const;

/** Anthropic models selectable for agents. The Claude CLI also accepts the short aliases. */
export const ANTHROPIC_MODELS: ModelOption[] = [
  { id: "claude-fable-5-1", label: "Claude Fable 5.1", description: "Most capable", vendor: "anthropic" },
  { id: "claude-opus-5-5", label: "Claude Opus 5.5", description: "Deep reasoning, complex work", vendor: "anthropic" },
  { id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5", description: "Balanced default", vendor: "anthropic" },
  { id: "claude-haiku-5-5", label: "Claude Haiku 5.5", description: "Fast and low cost", vendor: "anthropic" },
  { id: "claude-opus-5", label: "Claude Opus 5", description: "Previous Opus: costs more than Opus 5.5", vendor: "anthropic" },
  { id: "claude-sonnet-5", label: "Claude Sonnet 5", description: "Previous Sonnet", vendor: "anthropic" },
  { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5", description: "Previous Haiku", vendor: "anthropic" },
  { id: "opus", label: "opus (alias)", description: "Latest Opus via the Claude CLI", vendor: "anthropic" },
  { id: "sonnet", label: "sonnet (alias)", description: "Latest Sonnet via the Claude CLI", vendor: "anthropic" },
  { id: "haiku", label: "haiku (alias)", description: "Latest Haiku via the Claude CLI", vendor: "anthropic" },
];

/**
 * OpenAI models selectable for agents, run by Codex. The ids and descriptions are the current generation of the
 * catalog Codex 0.165.0 ships (`codex debug models`, read 2026-10-29), the version the image installs: what Codex
 * accepts, never guessed. A signed-in account may be offered a different list; that is checked when one signs in.
 */
export const OPENAI_MODELS: ModelOption[] = [
  { id: "gpt-6-astra", label: "GPT-6 Astra", description: "Frontier intelligence for the most demanding work", vendor: "openai" },
  { id: "gpt-6.1-sol", label: "GPT-6.1 Sol", description: "Latest workhorse model for coding and everyday work", vendor: "openai" },
  { id: "gpt-6.1-terra", label: "GPT-6.1 Terra", description: "Codex model at a lower price than Sol", vendor: "openai" },
  { id: "gpt-6-sol", label: "GPT-6 Sol", description: "Previous generation workhorse model", vendor: "openai" },
  { id: "gpt-6-luna", label: "GPT-6 Luna", description: "Fast and affordable model for easier tasks", vendor: "openai" },
];

/** Every model an agent can be set to, by vendor. */
export const MODELS: ModelOption[] = [...ANTHROPIC_MODELS, ...OPENAI_MODELS];

/** The models by their own names, without the CLI's aliases: what a schedule's owner picks from. */
export const NAMED_MODELS: ModelOption[] = MODELS.filter((m) => !m.label.includes("(alias)"));

export const DEFAULT_MODEL = "claude-sonnet-5-5";

export function modelLabel(id: string): string {
  return MODELS.find((m) => m.id === id)?.label ?? id;
}

/**
 * Which vendor runs a model: the registry's, and for an id it does not list (an admin's custom one) OpenAI's
 * naming — gpt-…, o<digit>…, codex-… — and Anthropic's for anything else, as every custom id was before Codex.
 */
export function vendorOf(model: string): ModelVendor {
  const known = MODELS.find((m) => m.id === model);
  if (known) return known.vendor;
  return /^(gpt-|o\d|codex-)/i.test(model) ? "openai" : "anthropic";
}

/**
 * The short names an agent gives when it chooses the model for a task it hands over, as the current ids. Fable is
 * named so that choosing it is refused for what it is, above the ceiling, rather than as a name nobody knows.
 */
export const MODEL_SHORT_NAMES: Record<string, string> = {
  haiku: "claude-haiku-5-5",
  sonnet: "claude-sonnet-5-5",
  opus: "claude-opus-5-5",
  fable: "claude-fable-5-1",
};

/** Models above what a delegated task may choose (Opus 5.5 is the most): only an agent's own default may be one. */
export const ABOVE_DELEGATION_CEILING = new Set(["claude-fable-5-1"]);
